// How many in-app notifications are unread, for the sidebar's Notifications row.
//
// ONE COUNT, SHARED, the shape clientEngagementStore uses next door: the
// sidebar asks on every page and the inbox changes the answer, so the number
// lives here and both read it rather than each fetching their own. The inbox
// publishes what it has just learned — a fresh list, a mark-as-read — so the
// badge follows without a second round trip or a reload.
//
// A FAILED FETCH LEAVES THE LAST ANSWER. The badge is a hint to open the inbox;
// flickering it to zero because one request failed would be telling somebody
// they have nothing to read when they do.

import { notificationService } from './notificationService';

type Listener = (count: number) => void;

let unread = 0;
let inFlight: Promise<void> | null = null;
const listeners = new Set<Listener>();

function publish(next: number) {
  unread = Math.max(0, next);
  listeners.forEach(listener => listener(unread));
}

export function getUnreadNotificationCount(): number {
  return unread;
}

export function subscribeUnreadNotificationCount(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Ask the api. Concurrent callers share one request. */
export function refreshUnreadNotificationCount(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = notificationService
    .getNotifications({ limit: 1, unread_only: true })
    .then(response => publish(response.unread_count))
    .catch(() => undefined)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** What the inbox already knows, so the badge follows without a fetch. */
export function setUnreadNotificationCount(count: number): void {
  publish(count);
}

/** Signed out: nothing is anybody's to read. */
export function clearUnreadNotificationCount(): void {
  publish(0);
}
